import { useCallback, useState } from "react";

import { message } from "contexts";
import { confirmDelete } from "pages/utils";
import { useMutation, useQuery } from "react-query";

import {
  fileDeleteRequest,
  fileDetailService,
  fileShareMemberListService,
  fileShareService,
} from "./services";

export const useShareFile = () => {
  const [{ mutate: onShareFile, isLoading: loading }] = [
    useMutation({
      mutationKey: "fileShareService",
      mutationFn: fileShareService,
    }),
  ];

  return {
    loading,
    onShareFile,
  };
};

export const useDeleteFile = (callback?: () => void) => {
  const [{ mutate: onDeleteFile, isLoading: loading }] = [
    useMutation({
      mutationKey: "fileDeleteRequest",
      mutationFn: fileDeleteRequest,
      onSuccess: () => {
        if (typeof callback === "function") {
          callback();
        }
      },
    }),
  ];

  const handleDeleteFile = useCallback(
    (name: string) => {
      confirmDelete(() => {
        onDeleteFile(name);
      });
    },
    [onDeleteFile]
  );

  return {
    loading,
    handleDeleteFile,
  };
};

export const useViewFile = () => {
  const [loading, setLoading] = useState(false);
  const [{ mutateAsync: getFileDetail }] = [
    useMutation({
      mutationKey: "fileDetailService",
      mutationFn: fileDetailService,
      onError: () => {
        setLoading(false);
        message({
          title: "File not found",
          type: "Error",
          buttonLabel: "Ok",
        });
      },
    }),
  ];

  const handleViewFile = useCallback(
    async (name: string, contentType?: string) => {
      setLoading(true);
      const res = await getFileDetail(name);

      if (contentType && contentType === "application/pdf") {
        const blob = new Blob([res as unknown as ArrayBuffer], {
          type: "application/pdf",
        });
        const blobURL = URL.createObjectURL(blob);
        window.open(blobURL);
      } else {
        const blob = new Blob([res as unknown as ArrayBuffer]);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setLoading(false);
    },
    [getFileDetail]
  );

  return {
    loading,
    handleViewFile,
  };
};

export const useGetListMemberShareFile = ({
  name,
  employeeId,
}: {
  name?: string;
  employeeId?: number;
}) => {
  const [{ data, isFetching: loading }] = [
    useQuery({
      queryKey: ["fileShareMemberListService", name, employeeId],
      enabled: !!name || !!employeeId,
      queryFn: () =>
        fileShareMemberListService({ name: name || "", employeeId }),
    }),
  ];

  return {
    loading,
    data: data?.data,
  };
};
